import { useDataProvider } from 'react-admin'
import { useQuery } from 'react-query'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import { CustomBarChart } from '../../components/customCharts/CustomBarChart'
import { CustomLineChart } from '../../components/customCharts/CustomLineChart'
import { getParamName, getParams } from '../../components/customCharts/helpers'
import { CustomChart as CustomChartType } from '../../components/customCharts/types'
import useChartContext from './useChartContext'
import { NoData } from '../NoData'

export const CustomChart = ({
  chart,
  heightClass = 'h-[400px]',
}: {
  chart: CustomChartType
  heightClass?: string
}) => {
  const dp = useDataProvider()
  const { query, dateRange } = useChartContext()

  const paramName = getParamName(chart.dataType)
  const ids = getParams(chart.items)

  const { data, isLoading } = useQuery(
    ['charts', 'getGetChart', chart.dataType, query, dateRange, ids],
    () =>
      dp.getChart(chart.dataType, {
        query,
        ...dateRange,
        [paramName]: ids,
      }),
    {
      staleTime: 0,
      cacheTime: 0,
    }
  )

  if (isLoading || !data?.data)
    return (
      <div
        className={`bg-gray-100 rounded-lg w-full ${heightClass} flex items-center justify-center`}
      >
        <div className="flex flex-col items-center gap-2">
          <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
          <span className="text-sm text-gray-500">Loading chart data...</span>
        </div>
      </div>
    )

  const chartData = data.data
  const hasData = Array.isArray(chartData)
    ? chartData.length > 0
    : Object.keys(chartData ?? {}).length > 0

  if (!hasData)
    return (
      <div className={heightClass}>
        <NoData />
      </div>
    )

  return (
    <div className={heightClass}>
      {chart.chartType === 'line' ? (
        <CustomLineChart data={chartData} chart={chart} />
      ) : (
        <CustomBarChart data={chartData} chart={chart} />
      )}
    </div>
  )
}
